import { FormEvent, useState } from 'react'

import * as api from 'api/api'
import { useAuth } from 'auth/AuthContext'
import { useConfirm } from 'components/Confirm'
import { SettingsTabs } from 'components/SettingsTabs'
import { Button, Card, ErrorState, Spinner, TextField } from 'components/ui'
import { useAction, useAsync } from 'lib/hooks'
import { t } from 'lib/i18n'
import { ForbiddenPage } from 'pages/system/ForbiddenPage'

/** Org-wide, single-tenant settings — JoPath has one organisation, so this is one real row, not a
 *  multi-tenant Org entity (Figma: Admin / 30 · Settings — General). */
export const GeneralSettingsPage = () => {
  const { user } = useAuth()
  const { data: settings, error, loading, reload } = useAsync(api.fetchAppSettings, [])
  const { busy, run } = useAction()
  const confirm = useConfirm()
  const [name, setName] = useState('')

  if (!user?.isAdmin) return <ForbiddenPage message={t('إعدادات النظام متاحة لمدير النظام فقط.', 'Settings are available to Admin only.')} />

  const save = async (event: FormEvent) => {
    event.preventDefault()
    if (!settings) return
    const done = await run('general', () => api.updateAppSettings({ ...settings, organisationName: name.trim() }), t('تم حفظ الاسم', 'Name saved'))
    if (done) reload()
  }

  const toggleMaintenance = async () => {
    if (!settings) return
    if (!settings.maintenanceMode) {
      const sure = await confirm(
        t(
          'سيتم منع كل المستخدمين (عدا مدير النظام) من إضافة أو تعديل أي بيانات حتى تُطفئ وضع الصيانة. المتابعة؟',
          'Everyone except Admin will be blocked from adding or changing data until you turn maintenance mode off. Continue?'
        ),
        {
          confirmLabel: t('تفعيل وضع الصيانة', 'Turn on maintenance mode'),
          tone: 'danger',
        }
      )
      if (!sure) return
    }
    const done = await run('maintenance', () => api.updateAppSettings({ ...settings, maintenanceMode: !settings.maintenanceMode }))
    if (done) reload()
  }

  const organisationName = settings?.organisationName ?? 'JoPath'

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>{t('الإعدادات', 'Settings')}</h1>
          <p className="muted">
            {t(`${organisationName} · التغييرات تُطبّق على كل مشروع وتُحفظ تلقائياً`, `${organisationName} · Changes apply to every project and are saved automatically`)}
          </p>
        </div>
      </div>

      <SettingsTabs />

      {loading ? (
        <Spinner />
      ) : error || !settings ? (
        <ErrorState message={error ?? t('تعذر تحميل الإعدادات', 'Could not load settings')} onRetry={reload} />
      ) : (
        <Card title={t('المنشأة', 'Organization')}>
          <form className="form-grid" onSubmit={save}>
            <TextField
              label={t('اسم المنشأة', 'Organization name')}
              value={name || settings.organisationName}
              onChange={(event) => setName(event.target.value)}
              required
            />
            <div className="form-actions span-2">
              <Button type="submit" variant="primary" busy={busy === 'general'} disabled={!name.trim() || name.trim() === settings.organisationName}>
                {t('حفظ', 'Save')}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {settings ? (
        <Card title={t('وضع الصيانة', 'Maintenance mode')}>
          <label className="checkbox">
            <input type="checkbox" checked={settings.maintenanceMode} disabled={busy === 'maintenance'} onChange={toggleMaintenance} />
            {t('تفعيل وضع الصيانة', 'Turn on maintenance mode')}
          </label>
          <p className="muted small">
            {t(
              'عند التفعيل، يصبح النظام للقراءة فقط لجميع المستخدمين عدا مدير النظام — لا يمكن إنشاء أو تعديل أي بيانات حتى يتم إيقافه.',
              'When this is on, the system is read-only for everyone except Admin. Nothing can be created or changed until it is turned off.'
            )}
          </p>
        </Card>
      ) : null}
    </div>
  )
}
